import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  GitBranch,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Shield,
  Trash2,
  Users,
  X,
} from "lucide-react";

import LoadingSpinner from "../../common/LoadingSpinner";
import { projectsAPI } from "../../../services/api";
import { PROJECT_ROLES } from "../../../constants/projectRoles";
import { isSystemAdmin } from "../../../constants/systemRoles";

const FIELD_ROLES = new Set([
  "operations_manager",
  "supervisor",
  "project_engineer",
  "general_foreman",
  "foreman",
]);

const ROLE_RANK = {
  operations_manager: 5,
  supervisor: 4,
  project_engineer: 3,
  general_foreman: 2,
  foreman: 1,
};

const getRoleLabel = (role) =>
  PROJECT_ROLES.find((r) => r.value === role)?.label || role || "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.email ||
  `User #${member?.user_id}`;

const emptyChart = (projectId) => ({
  id: null,
  project_id: projectId,
  version: 0,
  status: "empty",
  lanes: [],
  nodes: [],
});

function ChartPreview({ chart }) {
  const lanes = Array.isArray(chart?.lanes) ? chart.lanes : [];
  const nodes = Array.isArray(chart?.nodes) ? chart.nodes : [];
  const oversight = nodes.filter((n) => n.role === "operations_manager");

  const nodeCard = (node) => (
    <div key={node.project_team_member_id} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="text-sm font-semibold text-gray-900">{node.name || `Member #${node.project_team_member_id}`}</div>
      <div className="mt-1 text-xs font-medium text-orange-700">{getRoleLabel(node.role)}</div>
      {node.reports_to_name ? (
        <div className="mt-1 text-[11px] text-gray-500">Primary manager: {node.reports_to_name}</div>
      ) : null}
      {Array.isArray(node.additional_oversight_managers) && node.additional_oversight_managers.length ? (
        <div className="mt-1 text-[11px] text-purple-700">
          Additional oversight: {node.additional_oversight_managers.map((manager) => manager.name).join(", ")}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {oversight.length > 0 ? (
        <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-purple-700">Field Operations Oversight</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{oversight.map(nodeCard)}</div>
        </div>
      ) : null}

      {lanes.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-gray-500">
          No organization lanes have been published yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {lanes.map((lane) => {
            const laneNodes = nodes
              .filter((n) => Number(n.lane_id) === Number(lane.id) && n.role !== "supervisor")
              .sort(
                (a, b) =>
                  (ROLE_RANK[b.role] || 0) - (ROLE_RANK[a.role] || 0) ||
                  Number(a.display_order || 0) - Number(b.display_order || 0),
              );
            return (
              <div key={lane.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <GitBranch className="h-4 w-4 text-orange-500" />
                    <h3 className="font-semibold text-gray-900">{lane.name}</h3>
                  </div>
                  {lane.supervisor_name ? (
                    <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800">
                      Supervisor: {lane.supervisor_name}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">No Supervisor</span>
                  )}
                </div>
                <div className="space-y-2">
                  {lane.supervisor_name ? (
                    <div className="rounded-xl border border-orange-200 bg-orange-50/70 p-3 shadow-sm">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">Lane Supervisor</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{lane.supervisor_name}</div>
                      <div className="mt-1 text-xs font-medium text-orange-700">Supervisor</div>
                      <div className="mt-1 text-[11px] text-gray-500">Accountable for this lane</div>
                    </div>
                  ) : null}
                  {laneNodes.length ? laneNodes.map(nodeCard) : (
                    <div className="rounded-xl border border-dashed bg-white p-4 text-sm text-gray-500">Lane created; team members can be added at any time.</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProjectTeamView({ projectId, systemRole, projectRole }) {
  const isExternalUser = systemRole === "user_external";
  const [section, setSection] = useState(isExternalUser ? "organization" : "members");
  const [members, setMembers] = useState([]);
  const [chart, setChart] = useState(emptyChart(projectId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftLanes, setDraftLanes] = useState([]);
  const [draftNodes, setDraftNodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);
  const [teamCandidates, setTeamCandidates] = useState([]);
  const [memberUserId, setMemberUserId] = useState("");
  const [memberRole, setMemberRole] = useState("foreman");
  const [teamSaving, setTeamSaving] = useState(false);

  const canManageOrg = isSystemAdmin(systemRole) || projectRole === "qa_qc_manager";
  const canManageTeam = isSystemAdmin(systemRole) || ["qa_qc_manager", "project_manager"].includes(projectRole);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [teamRes, chartRes, candidateRes] = await Promise.all([
        isExternalUser ? Promise.resolve({ data: [] }) : projectsAPI.getTeam(projectId),
        projectsAPI.getOrgChart(projectId),
        canManageTeam ? projectsAPI.getTeamCandidates(projectId) : Promise.resolve({ data: [] }),
      ]);
      setMembers(Array.isArray(teamRes.data) ? teamRes.data : []);
      setChart(chartRes.data || emptyChart(projectId));
      setTeamCandidates(Array.isArray(candidateRes.data) ? candidateRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load project team.");
    } finally {
      setLoading(false);
    }
  }, [projectId, isExternalUser, canManageTeam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isExternalUser) setSection("organization");
  }, [isExternalUser]);

  const activeMembers = useMemo(
    () => members.filter((m) => m.is_active !== false),
    [members],
  );

  const memberByTeamId = useMemo(() => {
    const map = new Map();
    activeMembers.forEach((m) => map.set(Number(m.id), m));
    return map;
  }, [activeMembers]);

  const assignProjectRole = async (userId, role) => {
    if (!projectId || !userId || !role || !canManageTeam) return;
    setTeamSaving(true);
    setError("");
    setSuccess("");
    try {
      await projectsAPI.addTeamMember(projectId, { user_id: Number(userId), role });
      setSuccess("Project role saved and audit logged.");
      setMemberUserId("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save project role.");
    } finally {
      setTeamSaving(false);
    }
  };

  const deactivateProjectMember = async (member) => {
    if (!canManageTeam || !member?.id) return;
    if (!window.confirm(`Deactivate ${memberName(member)} from this project?`)) return;
    setTeamSaving(true);
    setError("");
    try {
      await projectsAPI.deactivateTeamMember(projectId, member.id);
      setSuccess("Project membership deactivated and audit logged.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to deactivate project member.");
    } finally {
      setTeamSaving(false);
    }
  };

  const beginEdit = () => {
    const laneRefById = new Map();
    const legacySupervisorByLaneId = new Map(
      (chart?.nodes || [])
        .filter((node) => node.role === "supervisor" && node.lane_id)
        .map((node) => [Number(node.lane_id), Number(node.project_team_member_id)]),
    );
    const lanes = (chart?.lanes || []).map((lane, index) => {
      const laneRef = `existing-${lane.id}`;
      laneRefById.set(Number(lane.id), laneRef);
      return {
        lane_ref: laneRef,
        name: lane.name,
        supervisor_team_member_id:
          lane.supervisor_team_member_id || legacySupervisorByLaneId.get(Number(lane.id)) || null,
        display_order: index,
      };
    });
    const nodes = (chart?.nodes || []).map((node, index) => ({
      project_team_member_id: Number(node.project_team_member_id),
      reports_to_team_member_id: node.reports_to_team_member_id ? Number(node.reports_to_team_member_id) : null,
      additional_oversight_manager_ids: Array.isArray(node.additional_oversight_manager_ids)
        ? node.additional_oversight_manager_ids.map(Number)
        : [],
      lane_ref:
        node.role === "supervisor"
          ? null
          : node.lane_id
            ? laneRefById.get(Number(node.lane_id))
            : null,
      display_order: index,
    }));
    setDraftLanes(lanes);
    setDraftNodes(nodes);
    setValidation(null);
    setEditing(true);
    setSection("organization");
  };

  const addLane = () => {
    const laneRef = `lane-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setDraftLanes((prev) => [
      ...prev,
      { lane_ref: laneRef, name: `New Lane ${prev.length + 1}`, supervisor_team_member_id: null, display_order: prev.length },
    ]);
  };

  const removeLane = (laneRef) => {
    const nodeIds = draftNodes
      .filter((n) => n.lane_ref === laneRef)
      .map((n) => n.project_team_member_id);
    setDraftNodes((prev) =>
      prev
        .filter((n) => n.lane_ref !== laneRef)
        .map((n) =>
          nodeIds.includes(n.reports_to_team_member_id)
            ? { ...n, reports_to_team_member_id: null }
            : n,
        ),
    );
    setDraftLanes((prev) => prev.filter((lane) => lane.lane_ref !== laneRef));
  };

  const usedMemberIds = useMemo(
    () => new Set(draftNodes.map((n) => Number(n.project_team_member_id))),
    [draftNodes],
  );

  const addMemberNode = (teamMemberId, laneRef) => {
    const id = Number(teamMemberId);
    if (!id || usedMemberIds.has(id)) return;
    const member = memberByTeamId.get(id);
    if (!member) return;
    const role = member.role;
    let parent = null;

    if (role === "supervisor") {
      const operationsManager = draftNodes.find((node) => {
        const candidate = memberByTeamId.get(Number(node.project_team_member_id));
        return candidate?.role === "operations_manager";
      });
      parent = operationsManager ? Number(operationsManager.project_team_member_id) : null;
    } else if (role === "project_engineer" && laneRef) {
      const lane = draftLanes.find((item) => item.lane_ref === laneRef);
      parent = lane?.supervisor_team_member_id
        ? Number(lane.supervisor_team_member_id)
        : null;
    } else {
      const candidates = draftNodes.filter((node) => {
        const candidate = memberByTeamId.get(Number(node.project_team_member_id));
        const candidateRole = candidate?.role;
        if (!candidateRole || (ROLE_RANK[candidateRole] || 0) <= (ROLE_RANK[role] || 0)) return false;
        return candidateRole === "operations_manager" || node.lane_ref === laneRef;
      });
      if (candidates.length) {
        candidates.sort((a, b) => {
          const ra = ROLE_RANK[memberByTeamId.get(Number(a.project_team_member_id))?.role] || 0;
          const rb = ROLE_RANK[memberByTeamId.get(Number(b.project_team_member_id))?.role] || 0;
          return ra - rb;
        });
        parent = Number(candidates[0].project_team_member_id);
      }
    }

    setDraftNodes((prev) => [
      ...prev,
      {
        project_team_member_id: id,
        reports_to_team_member_id: parent,
        additional_oversight_manager_ids: [],
        lane_ref: role === "operations_manager" || role === "supervisor" ? null : laneRef,
        display_order: prev.length,
      },
    ]);
  };

  const assignLaneSupervisor = (laneRef, teamMemberId) => {
    const supervisorId = teamMemberId ? Number(teamMemberId) : null;
    const previousSupervisorId = draftLanes.find((lane) => lane.lane_ref === laneRef)?.supervisor_team_member_id || null;

    setDraftLanes((prev) =>
      prev.map((lane) =>
        lane.lane_ref === laneRef
          ? { ...lane, supervisor_team_member_id: supervisorId }
          : lane,
      ),
    );

    // Keep direct PE reporting aligned with lane ownership when a lane Supervisor changes.
    setDraftNodes((prev) =>
      prev.map((node) => {
        if (node.lane_ref !== laneRef) return node;
        const member = memberByTeamId.get(Number(node.project_team_member_id));
        if (member?.role !== "project_engineer") return node;
        const currentParent = Number(node.reports_to_team_member_id || 0);
        const shouldFollowLaneSupervisor =
          !currentParent ||
          currentParent === Number(previousSupervisorId || 0);
        return shouldFollowLaneSupervisor
          ? { ...node, reports_to_team_member_id: supervisorId }
          : node;
      }),
    );

    if (!supervisorId || usedMemberIds.has(supervisorId)) return;
    const supervisor = memberByTeamId.get(supervisorId);
    if (supervisor?.role !== "supervisor") return;
    addMemberNode(supervisorId, null);
  };

  const removeNode = (memberId) => {
    setDraftNodes((prev) =>
      prev
        .filter((n) => Number(n.project_team_member_id) !== Number(memberId))
        .map((n) => {
          const next = Number(n.reports_to_team_member_id) === Number(memberId)
            ? { ...n, reports_to_team_member_id: null }
            : n;
          return {
            ...next,
            additional_oversight_manager_ids: (next.additional_oversight_manager_ids || []).filter(
              (id) => Number(id) !== Number(memberId),
            ),
          };
        }),
    );
    setDraftLanes((prev) =>
      prev.map((lane) =>
        Number(lane.supervisor_team_member_id) === Number(memberId)
          ? { ...lane, supervisor_team_member_id: null }
          : lane,
      ),
    );
  };

  const payload = useMemo(
    () => ({
      lanes: draftLanes.map((lane, index) => ({ ...lane, display_order: index })),
      nodes: draftNodes.map((node, index) => ({ ...node, display_order: index })),
    }),
    [draftLanes, draftNodes],
  );

  const validateDraft = async () => {
    const res = await projectsAPI.validateOrgChart(projectId, payload);
    setValidation(res.data);
    return res.data;
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await validateDraft();
      if (!result?.valid) return;
      const res = await projectsAPI.saveOrgChart(projectId, payload);
      setChart(res.data || emptyChart(projectId));
      setSuccess(`Organization chart version ${res.data?.version || ""} published.`);
      setEditing(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || err?.message || "Failed to save organization chart.");
    } finally {
      setSaving(false);
    }
  };

  const previewChart = useMemo(() => {
    const syntheticLanes = draftLanes.map((lane, index) => {
      const supervisor = memberByTeamId.get(Number(lane.supervisor_team_member_id));
      return {
        id: index + 1,
        name: lane.name,
        display_order: index,
        lane_ref: lane.lane_ref,
        supervisor_team_member_id: lane.supervisor_team_member_id || null,
        supervisor_user_id: supervisor?.user_id || null,
        supervisor_name: supervisor ? memberName(supervisor) : null,
        supervisor_email: supervisor?.email || null,
      };
    });
    const laneIdByRef = new Map(syntheticLanes.map((l) => [l.lane_ref, l.id]));
    const nodes = draftNodes.map((node) => {
      const member = memberByTeamId.get(Number(node.project_team_member_id));
      const parent = memberByTeamId.get(Number(node.reports_to_team_member_id));
      return {
        ...node,
        user_id: member?.user_id,
        role: member?.role,
        name: memberName(member),
        email: member?.email,
        reports_to_name: parent ? memberName(parent) : null,
        reports_to_role: parent?.role,
        additional_oversight_managers: (node.additional_oversight_manager_ids || [])
          .map((id) => memberByTeamId.get(Number(id)))
          .filter(Boolean)
          .map((manager) => ({
            project_team_member_id: manager.id,
            user_id: manager.user_id,
            name: memberName(manager),
            email: manager.email,
          })),
        lane_id: node.lane_ref ? laneIdByRef.get(node.lane_ref) : null,
      };
    });
    return { lanes: syntheticLanes, nodes };
  }, [draftLanes, draftNodes, memberByTeamId]);

  if (loading) {
    return <div className="flex h-56 items-center justify-center"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><Users className="h-6 w-6 text-orange-500" /> Project Team</h2>
          <p className="text-sm text-gray-500">Team directory and editable reporting lanes for field workflow control.</p>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="flex gap-2 border-b">
        {!isExternalUser ? (
          <button type="button" onClick={() => setSection("members")} className={`px-4 py-2 text-sm font-semibold ${section === "members" ? "border-b-2 border-orange-500 text-orange-700" : "text-gray-500"}`}>Members</button>
        ) : null}
        <button type="button" onClick={() => setSection("organization")} className={`px-4 py-2 text-sm font-semibold ${section === "organization" ? "border-b-2 border-orange-500 text-orange-700" : "text-gray-500"}`}>Organization Chart</button>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div> : null}

      {section === "members" ? (
        <div className="space-y-4">
          {canManageTeam ? (
            <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
              <div className="mb-3">
                <h3 className="font-semibold text-gray-900">Assign project role</h3>
                <p className="text-xs text-gray-500">QA/QC Managers can add existing users, assign roles, and update existing project roles. Every change is audit logged.</p>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_240px_auto]">
                <select value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select user</option>
                  {teamCandidates.map((user) => (
                    <option key={user.id} value={user.id}>{`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email} — {user.email}</option>
                  ))}
                </select>
                <select value={memberRole} onChange={(e) => setMemberRole(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                  {PROJECT_ROLES.filter((role) => role.value !== "project_manager" || isSystemAdmin(systemRole)).map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
                <button type="button" disabled={teamSaving || !memberUserId} onClick={() => assignProjectRole(memberUserId, memberRole)} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {teamSaving ? "Saving..." : "Assign / Update"}
                </button>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="grid grid-cols-12 gap-3 border-b bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <div className="col-span-4">Member</div><div className="col-span-3">Role</div><div className="col-span-2">Status</div><div className="col-span-2">Assigned</div><div className="col-span-1">Action</div>
            </div>
            <div className="divide-y">
              {members.map((member) => (
                <div key={member.id} className="grid grid-cols-12 items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <div className="col-span-4 min-w-0"><div className="truncate text-sm font-semibold text-gray-900">{memberName(member)}</div>{member.email ? <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500"><Mail className="h-3 w-3" /> {member.email}</div> : null}</div>
                  <div className="col-span-3">
                    {canManageTeam && member.is_active !== false ? (
                      <select disabled={teamSaving || (member.role === "project_manager" && !isSystemAdmin(systemRole))} value={member.role} onChange={(e) => assignProjectRole(member.user_id, e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs">
                        {PROJECT_ROLES.filter((role) => role.value !== "project_manager" || isSystemAdmin(systemRole) || member.role === "project_manager").map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                      </select>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">{getRoleLabel(member.role)}</span>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-gray-600">{member.is_active !== false ? "Active" : "Inactive"}</div>
                  <div className="col-span-2 text-xs text-gray-600">{formatDate(member.assigned_date)}</div>
                  <div className="col-span-1">
                    {canManageTeam && member.is_active !== false && member.role !== "project_manager" ? (
                      <button type="button" onClick={() => deactivateProjectMember(member)} className="text-xs font-semibold text-red-600 hover:underline">Remove</button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div>
              <div className="flex items-center gap-2"><GitBranch className="h-5 w-5 text-orange-500" /><h3 className="font-semibold text-gray-900">Published Organization Chart</h3></div>
              <p className="mt-1 text-xs text-gray-500">Version {chart?.version || 0}. Lane names and reporting lines can be changed at any time by publishing a new version.</p>
            </div>
            {canManageOrg && !editing ? <button type="button" onClick={beginEdit} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black"><Pencil className="h-4 w-4" /> Edit Chart</button> : null}
          </div>

          {editing ? (
            <div className="space-y-4 rounded-2xl border border-orange-200 bg-orange-50/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><h3 className="font-semibold text-gray-900">Edit Organization Structure</h3><p className="text-xs text-gray-500">Add or rename lanes, assign people, and set reporting relationships. Saving publishes a new version.</p></div>
                <div className="flex gap-2"><button type="button" onClick={addLane} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm hover:bg-gray-50"><Plus className="h-4 w-4" /> Add Lane</button><button type="button" onClick={() => setEditing(false)} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm"><X className="h-4 w-4" /> Cancel</button><button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60">{saving ? <LoadingSpinner size="small" /> : <Save className="h-4 w-4" />} Validate & Publish</button></div>
              </div>

              <div className="rounded-xl border border-purple-100 bg-purple-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-purple-800"><Shield className="h-4 w-4" /> Field Operations Managers</div>
                <select value="" onChange={(e) => { addMemberNode(e.target.value, null); e.target.value = ""; }} className="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm">
                  <option value="">Add Operations Manager…</option>
                  {activeMembers.filter((m) => m.role === "operations_manager" && !usedMemberIds.has(Number(m.id))).map((m) => <option key={m.id} value={m.id}>{memberName(m)}</option>)}
                </select>
                <div className="mt-2 space-y-2">
                  {draftNodes.filter((n) => {
                    const member = memberByTeamId.get(Number(n.project_team_member_id));
                    return member?.role === "operations_manager";
                  }).map((node) => {
                    const member = memberByTeamId.get(Number(node.project_team_member_id));
                    return <div key={node.project_team_member_id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm"><span><strong>{memberName(member)}</strong> · {getRoleLabel(member?.role)}</span><button type="button" onClick={() => removeNode(node.project_team_member_id)} className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></div>;
                  })}
                </div>
              </div>

              <div className="rounded-xl border border-orange-100 bg-orange-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-orange-800"><Shield className="h-4 w-4" /> Supervisors</div>
                <p className="mb-3 text-xs text-orange-700">A Supervisor is added once to the graph and may own multiple lanes. Assign lane ownership inside each lane card.</p>
                <div className="space-y-2">
                  {draftNodes.filter((n) => {
                    const member = memberByTeamId.get(Number(n.project_team_member_id));
                    return member?.role === "supervisor";
                  }).map((node) => {
                    const member = memberByTeamId.get(Number(node.project_team_member_id));
                    const opsManagers = draftNodes.filter((candidate) => {
                      const cm = memberByTeamId.get(Number(candidate.project_team_member_id));
                      return cm?.role === "operations_manager";
                    });
                    const laneCount = draftLanes.filter((lane) => Number(lane.supervisor_team_member_id) === Number(node.project_team_member_id)).length;
                    return (
                      <div key={node.project_team_member_id} className="rounded-lg bg-white px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span><strong>{memberName(member)}</strong> · {laneCount} lane{laneCount === 1 ? "" : "s"}</span>
                          <button type="button" onClick={() => removeNode(node.project_team_member_id)} className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Primary Operations Manager</label>
                        <select value={node.reports_to_team_member_id || ""} onChange={(e) => setDraftNodes((prev) => prev.map((x) => Number(x.project_team_member_id) === Number(node.project_team_member_id) ? {
                          ...x,
                          reports_to_team_member_id: e.target.value ? Number(e.target.value) : null,
                          additional_oversight_manager_ids: (x.additional_oversight_manager_ids || []).filter((id) => Number(id) !== Number(e.target.value || 0)),
                        } : x))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs">
                          <option value="">No Primary Operations Manager selected</option>
                          {opsManagers.map((candidate) => { const cm = memberByTeamId.get(Number(candidate.project_team_member_id)); return <option key={candidate.project_team_member_id} value={candidate.project_team_member_id}>{memberName(cm)}</option>; })}
                        </select>
                        <label className="mt-3 block text-[11px] font-semibold uppercase tracking-wide text-purple-600">Additional Oversight Managers</label>
                        <div className="mt-1 grid gap-1 rounded-lg border border-purple-100 bg-purple-50/60 p-2 sm:grid-cols-2">
                          {opsManagers
                            .filter((candidate) => Number(candidate.project_team_member_id) !== Number(node.reports_to_team_member_id || 0))
                            .map((candidate) => {
                              const cm = memberByTeamId.get(Number(candidate.project_team_member_id));
                              const checked = (node.additional_oversight_manager_ids || []).some((id) => Number(id) === Number(candidate.project_team_member_id));
                              return (
                                <label key={candidate.project_team_member_id} className="flex cursor-pointer items-center gap-2 rounded-md bg-white px-2 py-1.5 text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => setDraftNodes((prev) => prev.map((x) => {
                                      if (Number(x.project_team_member_id) !== Number(node.project_team_member_id)) return x;
                                      const current = new Set((x.additional_oversight_manager_ids || []).map(Number));
                                      if (e.target.checked) current.add(Number(candidate.project_team_member_id));
                                      else current.delete(Number(candidate.project_team_member_id));
                                      return { ...x, additional_oversight_manager_ids: Array.from(current) };
                                    }))}
                                  />
                                  <span>{memberName(cm)}</span>
                                </label>
                              );
                            })}
                          {opsManagers.length <= 1 ? (
                            <span className="text-[11px] text-purple-700">Add another Operations Manager to enable additional oversight.</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {draftLanes.map((lane, laneIndex) => {
                  const laneNodes = draftNodes.filter((n) => n.lane_ref === lane.lane_ref);
                  const available = activeMembers.filter((m) => FIELD_ROLES.has(m.role) && !["operations_manager", "supervisor"].includes(m.role) && !usedMemberIds.has(Number(m.id)));
                  return (
                    <div key={lane.lane_ref} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2">
                        <input value={lane.name} onChange={(e) => setDraftLanes((prev) => prev.map((x) => x.lane_ref === lane.lane_ref ? { ...x, name: e.target.value } : x))} className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 font-semibold text-gray-900 focus:ring-2 focus:ring-orange-500" />
                        <button type="button" onClick={() => removeLane(lane.lane_ref)} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50" title="Remove lane"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-3 rounded-xl border border-orange-100 bg-orange-50 p-3">
                        <label className="block text-[11px] font-semibold uppercase tracking-wide text-orange-700">Lane Supervisor</label>
                        <select value={lane.supervisor_team_member_id || ""} onChange={(e) => assignLaneSupervisor(lane.lane_ref, e.target.value)} className="mt-1 w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm">
                          <option value="">No Supervisor assigned</option>
                          {activeMembers.filter((m) => m.role === "supervisor").map((m) => <option key={m.id} value={m.id}>{memberName(m)}</option>)}
                        </select>
                        <p className="mt-1 text-[11px] text-orange-700">The same Supervisor can be selected for another lane.</p>
                      </div>
                      <div className="mt-3">
                        <select value="" onChange={(e) => { addMemberNode(e.target.value, lane.lane_ref); e.target.value = ""; }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                          <option value="">Add P.E. / GF / Foreman…</option>
                          {available.sort((a, b) => (ROLE_RANK[b.role] || 0) - (ROLE_RANK[a.role] || 0)).map((m) => <option key={m.id} value={m.id}>{memberName(m)} — {getRoleLabel(m.role)}</option>)}
                        </select>
                      </div>
                      <div className="mt-3 space-y-2">
                        {laneNodes.sort((a, b) => (ROLE_RANK[memberByTeamId.get(Number(b.project_team_member_id))?.role] || 0) - (ROLE_RANK[memberByTeamId.get(Number(a.project_team_member_id))?.role] || 0)).map((node) => {
                          const member = memberByTeamId.get(Number(node.project_team_member_id));
                          const parentOptions = draftNodes.filter((candidate) => {
                            if (Number(candidate.project_team_member_id) === Number(node.project_team_member_id)) return false;
                            const candidateMember = memberByTeamId.get(Number(candidate.project_team_member_id));
                            if ((ROLE_RANK[candidateMember?.role] || 0) <= (ROLE_RANK[member?.role] || 0)) return false;
                            if (candidateMember?.role === "operations_manager") return true;
                            if (candidateMember?.role === "supervisor") {
                              return Number(candidate.project_team_member_id) === Number(lane.supervisor_team_member_id);
                            }
                            return candidate.lane_ref === lane.lane_ref;
                          });
                          return (
                            <div key={node.project_team_member_id} className="rounded-xl border bg-gray-50 p-3">
                              <div className="flex items-start justify-between gap-2"><div><div className="text-sm font-semibold text-gray-900">{memberName(member)}</div><div className="text-xs text-orange-700">{getRoleLabel(member?.role)}</div></div><button type="button" onClick={() => removeNode(node.project_team_member_id)} className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></div>
                              <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reports To</label>
                              <select value={node.reports_to_team_member_id || ""} onChange={(e) => setDraftNodes((prev) => prev.map((x) => Number(x.project_team_member_id) === Number(node.project_team_member_id) ? { ...x, reports_to_team_member_id: e.target.value ? Number(e.target.value) : null } : x))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs">
                                <option value="">No parent selected</option>
                                {parentOptions.map((candidate) => { const cm = memberByTeamId.get(Number(candidate.project_team_member_id)); return <option key={candidate.project_team_member_id} value={candidate.project_team_member_id}>{memberName(cm)} — {getRoleLabel(cm?.role)}</option>; })}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {validation ? (
                <div className={`rounded-xl border p-3 text-sm ${validation.valid ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>
                  <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> {validation.valid ? "Chart structure is valid" : "Chart needs correction"}</div>
                  {(validation.errors || []).map((item) => <div key={item} className="mt-1">• {item}</div>)}
                  {(validation.warnings || []).map((item) => <div key={item} className="mt-1 text-amber-700">• {item}</div>)}
                </div>
              ) : null}

              <div><h4 className="mb-2 text-sm font-semibold text-gray-900">Live Preview</h4><ChartPreview chart={previewChart} /></div>
            </div>
          ) : (
            <ChartPreview chart={chart} />
          )}
        </div>
      )}
    </div>
  );
}
